// Ekran 05 — Üniteler.
// Sınıf adları ref_files/btys-erp-05-uniteler.html'den birebir alındı.

import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import Kabuk from '../../components/Kabuk'
import { useDrawer } from '../../components/Drawer'
import { useSekmeler } from '../../components/Sekmeler'
import { toast } from '../../components/Toast'
import { useFitInsp } from '../../lib/useFitInsp'
import { sayiyaCevirBos, tr } from '../../lib/trFormat'
import { ApiHatasi } from '../../lib/api'
import { useYetki, YetkiKapisi } from '../../lib/yetki'
import SablonDrawer, { FrekSecici } from './SablonDrawer'
import UniteDetaySekmesi from './UniteDetaySekmesi'
import {
  FREK_ETIKET,
  GRUP_ETIKET,
  frekOzeti,
  uniteler as uniteApi,
  type UniteGrubu,
  type UniteOzet,
  type UniteSinifiDto,
} from './api'
import Sayfalayici from '../../components/Sayfalayici'

const SEGMENTLER: { anahtar: UniteGrubu | 'tumu'; etiket: string }[] = [
  { anahtar: 'tumu', etiket: 'Tümü' },
  { anahtar: 'on', etiket: 'Ön İşlem' },
  { anahtar: 'cur', etiket: 'Çürütme' },
  { anahtar: 'depo', etiket: 'Depo' },
  { anahtar: 'gaz', etiket: 'Gaz' },
  { anahtar: 'enerji', etiket: 'Enerji' },
]

/** Kardeş ekranlarla (01/02/03/04) aynı sayfa boyu. */
const SAYFA_BOYU = 25

/**
 * Teknik değer serbest metin: sayıysa TR biçimiyle basılır (3000 → "3.000"),
 * değilse ham metin görünür (GA-1 → "Şartlandırıcı çıkışı"). Biçimleme yalnız
 * trFormat üzerinden geçer (§12).
 */
function teknikMetin(deger: string | null | undefined): string {
  if (deger === null || deger === undefined || deger.trim() === '') return '—'
  const sayi = sayiyaCevirBos(deger)
  return sayi === null ? deger : tr(sayi, 0)
}

/**
 * Tablodaki "Kapasite" sütunu — referans :519 `${u.T[0]} ${s.T[0][1]}`: sınıfın
 * 1. teknik alanının DEĞERİ + BİRİMİ.
 *
 * Alan sözlüğün ilk anahtarından değil ŞABLONDAN okunuyor: JSON'da anahtar
 * sırası garantili değil (kararlar-ek.md). Birim "-" olsa bile basılır —
 * GA-1'in "Şartlandırıcı çıkışı -" hücresi referans davranıştır.
 */
function kapasite(u: UniteOzet, sinif: UniteSinifiDto | undefined): string {
  const alan = sinif?.teknikAlanlar[0]
  if (!alan) return '—'
  return `${teknikMetin(u.teknikDegerler[alan.anahtar])} ${alan.birim ?? ''}`.trimEnd()
}

function Icerik() {
  const [grup, setGrup] = useState<UniteGrubu | 'tumu'>('tumu')
  const [arama, setArama] = useState('')
  const [sayfa, setSayfa] = useState(1)
  const [seciliId, setSeciliId] = useState<string | null>(null)
  const panel = useFitInsp<HTMLDivElement>()
  const { ac } = useDrawer()
  const sekmeler = useSekmeler()
  const kuyruk = useQueryClient()
  const { izinVar } = useYetki()
  const yazabilir = izinVar('Uniteler', 'yaz')

  /**
   * Referans `openFull()` (:629-631): satır çift tık · satır sonu ↗ · panel
   * altlığı ↗ — üçü de aynı tam sayfa sekmesini açar (id "c-<kod>").
   */
  const tamSayfaAc = (u: UniteOzet) =>
    sekmeler.ac({
      id: `c-${u.kod}`,
      baslik: u.kod,
      icerik: <UniteDetaySekmesi uniteId={u.id} kapat={() => sekmeler.kapat(`c-${u.kod}`)} />,
    })

  const { data: ozet } = useQuery({ queryKey: ['unite-ozeti'], queryFn: uniteApi.ozet })
  const { data: hepsi = [], isLoading } = useQuery({
    queryKey: ['uniteler'],
    queryFn: () => uniteApi.liste(),
  })

  // Aynı queryKey SablonDrawer ve UniteDetaySekmesi'nde de kullanılıyor —
  // önbellek paylaşımlı, bu sorgu ek ağ isteği doğurmuyor.
  const { data: siniflar = [] } = useQuery({
    queryKey: ['unite-siniflari'],
    queryFn: uniteApi.siniflar,
  })
  const sinifSozlugu = useMemo(() => new Map(siniflar.map((s) => [s.id, s])), [siniflar])

  const liste = hepsi.filter((u) => {
    if (grup !== 'tumu' && u.grup !== grup) return false
    if (arama) {
      const a = arama.toLocaleLowerCase('tr')
      if (![u.kod, u.ad, u.sinifAd].join(' ').toLocaleLowerCase('tr').includes(a)) return false
    }
    return true
  })

  // Sayaçlar filtreden BAĞIMSIZ (referans drawSeg).
  const sayac = (a: UniteGrubu | 'tumu') =>
    a === 'tumu' ? hepsi.length : hepsi.filter((u) => u.grup === a).length

  const sayfaSayisi = Math.max(1, Math.ceil(liste.length / SAYFA_BOYU))
  const gecerliSayfa = Math.min(sayfa, sayfaSayisi)
  const sayfadakiler = liste.slice((gecerliSayfa - 1) * SAYFA_BOYU, gecerliSayfa * SAYFA_BOYU)

  // Seçim GÖRÜNEN sayfadan kurulur; yoksa panel listede olmayan bir satırı gösterir.
  const ilkId = sayfadakiler[0]?.id
  useEffect(() => {
    if (ilkId && !sayfadakiler.some((u) => u.id === seciliId)) setSeciliId(ilkId)
  }, [ilkId, sayfadakiler, seciliId])

  const { data: detay } = useQuery({
    queryKey: ['unite', seciliId],
    queryFn: () => uniteApi.detay(seciliId!),
    enabled: !!seciliId,
  })

  const seciliSinif = detay ? sinifSozlugu.get(detay.unite.uniteSinifiId) : undefined

  /**
   * Panelin "Teknik Parametreler (T) — hızlı düzenle" bloğunun yerel taslağı.
   *
   * Sunucudan gelen hâl JSON'a çevrilip anahtar olarak tutuluyor: sıklık kaydı
   * sonrası tetiklenen yeniden çekim teknik değerleri DEĞİŞTİRMEDİĞİ sürece
   * kullanıcının yazdığı silinmesin (referans paneli innerHTML ile yeniden kurup
   * girişleri siliyordu — o yan etki bilerek taşınmadı).
   */
  const teknikJson = detay ? JSON.stringify(detay.unite.teknikDegerler) : ''
  const temel = useMemo<Record<string, string>>(() => {
    if (!teknikJson) return {}
    const ham = JSON.parse(teknikJson) as Record<string, string | null>
    return Object.fromEntries(Object.entries(ham).map(([k, v]) => [k, v ?? '']))
  }, [teknikJson])

  const [taslak, setTaslak] = useState<{ kaynak: string; alanlar: Record<string, string> }>({
    kaynak: '',
    alanlar: {},
  })
  const alanlar = taslak.kaynak === teknikJson ? taslak.alanlar : temel
  const alanYaz = (anahtar: string, deger: string) =>
    setTaslak({ kaynak: teknikJson, alanlar: { ...alanlar, [anahtar]: deger } })

  const teknikDegisti = (seciliSinif?.teknikAlanlar ?? []).some(
    (a) => (alanlar[a.anahtar] ?? '') !== (temel[a.anahtar] ?? ''),
  )

  const teknikKaydet = useMutation({
    mutationFn: () =>
      uniteApi.guncelle(detay!.unite.id, {
        kod: detay!.unite.kod,
        ad: detay!.unite.ad,
        uniteSinifiId: detay!.unite.uniteSinifiId,
        durum: detay!.unite.durum,
        // Alanlar ŞABLON sırasıyla yazılır; ham metin gider (Rejim gibi metin
        // değerler sayıya çevrilirse sunucuda düşerdi).
        teknikDegerler: Object.fromEntries(
          (seciliSinif?.teknikAlanlar ?? []).map((a) => [a.anahtar, alanlar[a.anahtar] ?? '']),
        ),
      }),
    onSuccess: (u) => {
      kuyruk.invalidateQueries({ queryKey: ['uniteler'] })
      kuyruk.invalidateQueries({ queryKey: ['unite'] })
      kuyruk.invalidateQueries({ queryKey: ['unite-ozeti'] })
      toast(`${u.kod} · teknik değerler kaydedildi`)
    },
    onError: (h) => toast(h instanceof ApiHatasi ? h.message : 'Ünite kaydedilemedi'),
  })

  const frekKaydet = useMutation({
    mutationFn: ({ id, parametreler }: {
      id: string
      parametreler: import('./api').ParametreIstek[]
    }) => uniteApi.parametreler(id, parametreler),
    onSuccess: (_, degisken) => {
      kuyruk.invalidateQueries({ queryKey: ['uniteler'] })
      kuyruk.invalidateQueries({ queryKey: ['unite'] })
      kuyruk.invalidateQueries({ queryKey: ['unite-ozeti'] })
      kuyruk.invalidateQueries({ queryKey: ['beyan'] })
      const p = degisken.parametreler.find((x) => x.frek !== undefined)
      toast(`${detay?.unite.kod} · ${p?.ad} → ${FREK_ETIKET[p?.frek ?? '']} (K23)`)
    },
    onError: (h) => toast(h instanceof ApiHatasi ? h.message : 'Sıklık kaydedilemedi'),
  })

  const sil = useMutation({
    mutationFn: (id: string) => uniteApi.sil(id),
    onSuccess: () => {
      kuyruk.invalidateQueries({ queryKey: ['uniteler'] })
      kuyruk.invalidateQueries({ queryKey: ['unite-ozeti'] })
      setSeciliId(null)
      toast('Ünite silindi')
    },
    onError: (h) => toast(h instanceof ApiHatasi ? h.message : 'Ünite silinemedi'),
  })

  return (
    <>
      <div className="crumb">
        Ana Veri <b>/ Üniteler</b>
      </div>

      <div className="page-h">
        <h1 className="page-t">Üniteler — Tesis Yapılanması</h1>
        <div className="page-actions">
          <button
            className="btn btn-ghost"
            type="button"
            disabled
            title="Faz 7 kapsamı dışında — dışa aktarma henüz bağlanmadı"
            onClick={() => undefined}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
            </svg>
            Excel’e aktar
          </button>
          <button
            className="btn btn-pri"
            type="button"
            disabled={!yazabilir}
            title={yazabilir ? undefined : 'Bu modülde yazma yetkiniz yok'}
            onClick={() =>
              ac({
                baslik: 'Şablondan ünite ekle',
                altBaslik: 'Adım 1/2 — sınıf seçin (16 hazır şablon · K25 + tesis ekleri)',
                boyut: 'md',
                icerik: <SablonDrawer />,
              })
            }
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14M5 12h14" />
            </svg>
            Şablondan ünite ekle
          </button>
        </div>
      </div>

      <div className="kstrip">
        <div className="k">
          <div className="k-l">Toplam Ünite</div>
          <div className="k-v">{ozet?.toplam ?? '—'}</div>
          <div className="k-s">16 sınıf şablonundan</div>
        </div>
        <div className="k">
          <div className="k-l">Çürütücü Hacmi</div>
          <div className="k-v">{tr(ozet?.toplamCurutucuHacmi, 0)}</div>
          <div className="k-s">m³ · toplam aktif hacim</div>
        </div>
        <div className="k">
          <div className="k-l">Kurulu Güç</div>
          <div className="k-v">{tr(ozet?.kuruluGuc, 0)}</div>
          <div className="k-s">kWe · kojenerasyon</div>
        </div>
        <div className="k">
          <div className="k-l">Saatlik Beyan Alanı</div>
          <div className="k-v">{ozet?.saatlikParametre ?? '—'}</div>
          <div className="k-s">parametre · her saat girilir (K4)</div>
        </div>
        <div className="k">
          <div className="k-l">Günlük Beyan Satırı</div>
          <div className="k-v">{tr(ozet?.gunlukHucre, 0)}</div>
          <div className="k-s">hücre/gün · olay bazlı hariç</div>
        </div>
      </div>

      <div className="list-grid">
        <div className="card">
          <div className="toolbar">
            <div className="seg">
              {SEGMENTLER.map((s) => (
                <button
                  key={s.anahtar}
                  type="button"
                  className={grup === s.anahtar ? 'seg-i on' : 'seg-i'}
                  onClick={() => { setGrup(s.anahtar); setSayfa(1) }}
                >
                  {s.etiket}
                  <small>{sayac(s.anahtar)}</small>
                </button>
              ))}
            </div>
            <div className="f-search">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
              <input
                placeholder="Ünite adı, kod, sınıf ara…"
                value={arama}
                onChange={(e) => { setArama(e.target.value); setSayfa(1) }}
              />
            </div>
            <div className="toolbar-right">
              <span>{liste.length} ünite</span>
            </div>
          </div>

          <table className="grid">
            <thead>
              <tr>
                <th style={{ width: 64 }}>Kod</th>
                <th>Ünite</th>
                <th style={{ width: 150 }}>Kapasite</th>
                <th className="num" style={{ width: 70 }}>Beyan Par.</th>
                <th style={{ width: 190 }}>Sıklık Dağılımı</th>
                <th className="num" style={{ width: 76 }}>Satır/Gün</th>
                <th style={{ width: 44 }} />
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={7} className="cell-sub" style={{ padding: 14 }}>Yükleniyor…</td></tr>
              )}
              {!isLoading && liste.length === 0 && (
                <tr><td colSpan={7} className="cell-sub" style={{ padding: 14 }}>Kayıt yok.</td></tr>
              )}
              {sayfadakiler.map((u) => (
                <tr
                  key={u.id}
                  className={seciliId === u.id ? 'sel' : ''}
                  onClick={() => setSeciliId(u.id)}
                  onDoubleClick={() => tamSayfaAc(u)}
                >
                  <td><span className="kod">{u.kod}</span></td>
                  <td>
                    <div className="cell-name">
                      {u.ad}
                      {u.durum === 'bakimda' && (
                        <span className="badge b-amber" style={{ marginLeft: 4 }}>Bakımda</span>
                      )}
                      {u.durum === 'pasif' && (
                        <span className="badge b-gray" style={{ marginLeft: 6 }}>Pasif</span>
                      )}
                    </div>
                    <div className="cell-sub">{u.sinifAd} · {GRUP_ETIKET[u.grup]}</div>
                  </td>
                  <td className="mono" style={{ fontSize: 11.5 }}>
                    {kapasite(u, sinifSozlugu.get(u.uniteSinifiId))}
                  </td>
                  <td className="num">{u.parametreSayisi}</td>
                  <td style={{ fontSize: 11, color: 'var(--muted)' }}>{frekOzeti(u)}</td>
                  <td className="num">{u.gunlukHucre || '—'}</td>
                  <td>
                    <div className="row-acts">
                      <button
                        className="mini-btn"
                        type="button"
                        title="Ünite detayı + beyan önizleme"
                        onClick={(e) => { e.stopPropagation(); tamSayfaAc(u) }}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14 21 3" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Referans :376-378 + renderRows sonu — altlık metni ünite ve sınıf
              sayısını söyler; K23 notu zaten panelin .inst-note'unda duruyor. */}
          <div className="grid-foot">
            <span>
              Toplam {ozet?.toplam ?? '—'} ünite · şablon kütüphanesi: {siniflar.length} sınıf (K25)
            </span>
            <Sayfalayici gecerli={gecerliSayfa} toplam={sayfaSayisi} git={setSayfa} />
          </div>
        </div>

        <aside className="insp card" ref={panel}>
          {!detay ? (
            <div className="insp-empty">Bir ünite seçin.</div>
          ) : (
            <>
              <div className="insp-h">
                <div className="insp-eyebrow">
                  <span className="kod">{detay.unite.kod}</span>
                  <span>·</span>
                  <span>{detay.unite.sinifAd}</span>
                  <span>·</span>
                  <span>{GRUP_ETIKET[detay.unite.grup]}</span>
                </div>
                <div className="insp-t">
                  {detay.unite.ad}{' '}
                  {detay.unite.durum === 'bakimda' ? (
                    <span className="badge b-amber">Bakımda</span>
                  ) : detay.unite.durum === 'pasif' ? (
                    <span className="badge b-gray">Pasif</span>
                  ) : (
                    <span className="badge b-navy"><span className="dot" />Aktif</span>
                  )}
                </div>
                {/* Referans :551 — başlığın 3. satırı sınıf açıklaması.
                    Açıklama boşsa satır hiç çizilmez, yoksa panel başlığında
                    boş bir şerit açılır. */}
                {seciliSinif?.aciklama && (
                  <div className="cell-sub" style={{ marginTop: 2 }}>{seciliSinif.aciklama}</div>
                )}
              </div>

              <div className="insp-b">
                {/* Referans :552-553 — alanlar SINIF ŞABLONUNDAN çizilir (jsonb
                    anahtar sırası garantisiz) ve etiket birimi parantezle taşır;
                    birim "-" ise parantez yazılmaz. */}
                <div className="insp-sec">
                  <div className="insp-sec-t">Teknik Parametreler (T) — hızlı düzenle</div>
                  <div className="fgrid">
                    {(seciliSinif?.teknikAlanlar ?? []).map((t) => (
                      <div className="fld" key={t.anahtar}>
                        <label>
                          {t.etiket}
                          {t.birim && t.birim !== '-' ? ` (${t.birim})` : ''}
                        </label>
                        <input
                          className="in mono"
                          placeholder="—"
                          readOnly={!yazabilir}
                          value={alanlar[t.anahtar] ?? ''}
                          onChange={(e) => alanYaz(t.anahtar, e.target.value)}
                        />
                      </div>
                    ))}
                    {(seciliSinif?.teknikAlanlar ?? []).length === 0 && (
                      <div className="cell-sub">Bu sınıfta teknik alan tanımlı değil.</div>
                    )}
                  </div>
                </div>

                <div className="insp-sec">
                  <div className="inst">
                    <div className="inst-t">Beyan Yükü — Bu Ünite</div>
                    <div className="inst-3">
                      <div className="inst-c">
                        <div className="inst-v">
                          {detay.parametreler.filter((p) => p.frek === '1').length}
                        </div>
                        <div className="inst-l">saatlik param.</div>
                      </div>
                      <div className="inst-c">
                        <div className="inst-v">{detay.unite.gunlukHucre || '—'}</div>
                        <div className="inst-l">satır / gün</div>
                      </div>
                      <div className="inst-c">
                        <div className="inst-v">
                          {detay.parametreler.filter((p) => p.frek === 'olay').length || '—'}
                        </div>
                        <div className="inst-l">olay bazlı</div>
                      </div>
                    </div>
                    <div className="inst-note">
                      Sıklıklar 24’ün bölenleridir; grid buna göre kurulur (K23)
                    </div>
                  </div>
                </div>

                <div className="insp-sec">
                  <div className="insp-sec-t">Beyan Parametreleri (B) — sıklığı buradan değiştir</div>
                  {detay.parametreler.map((p) => (
                    <div className="bp-row" key={p.id}>
                      <span>{p.ad}</span>
                      <span className="bpu">{p.birim ?? '—'}</span>
                      <FrekSecici
                        deger={p.frek}
                        kaynak={p.kaynak}
                        degisti={(v) => {
                          if (!yazabilir) return
                          frekKaydet.mutate({
                            id: detay.unite.id,
                            parametreler: detay.parametreler.map((x) => ({
                              id: x.id, ad: x.ad, birim: x.birim,
                              frek: x.id === p.id ? v : x.frek,
                              kaynak: x.kaynak, sira: x.sira,
                            })),
                          })
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="insp-f">
                <button
                  className="btn btn-sec btn-sm"
                  type="button"
                  title="Detay + beyan önizleme"
                  style={{ padding: '0 8px' }}
                  onClick={() => tamSayfaAc(detay.unite)}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14 21 3" />
                  </svg>
                </button>
                <button
                  className="btn btn-danger btn-sm"
                  type="button"
                  disabled={!yazabilir || sil.isPending}
                  title={yazabilir ? 'Üniteyi sil' : 'Yazma yetkiniz yok'}
                  onClick={() => sil.mutate(detay.unite.id)}
                >
                  Sil
                </button>
                <button
                  className="btn btn-pri btn-sm"
                  type="button"
                  style={{ flex: 1, justifyContent: 'center' }}
                  disabled={!yazabilir || !teknikDegisti || teknikKaydet.isPending}
                  title={
                    !yazabilir
                      ? 'Bu modülde yazma yetkiniz yok'
                      : teknikDegisti
                        ? 'Teknik değerleri kaydet'
                        : 'Değişiklik yok — kaydedilecek bir şey bulunmuyor'
                  }
                  onClick={() => teknikKaydet.mutate()}
                >
                  {teknikKaydet.isPending ? 'Kaydediliyor…' : 'Kaydet'}
                </button>
              </div>
            </>
          )}
        </aside>
      </div>
    </>
  )
}

export default function UnitelerSayfasi() {
  return (
    <Kabuk listeBasligi="Üniteler">
      <YetkiKapisi modul="Uniteler">
        <Icerik />
      </YetkiKapisi>
    </Kabuk>
  )
}
